import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthShell from '../main/AuthShell';
import Icon from '../../components/common/Icon';
import StorefrontHelp from './StorefrontHelp';
import { apiRequest, handleBranding, shortDate, groupSponsorsByType } from '../../utils';
import { readCart, writeCart, cartCount } from '../shop/cart';
import './Storefront.css';

/* Public brand storefront (/:slug) — e.g. tornea.app/pinkladiesgamesdubai.
   No account needed: the brand's upcoming events, and per event the ticket
   catalog with package + per-seat prices. "Add" builds an event-scoped cart
   (checkout buys the whole package(s)); a team rate also offers "buy only my
   seat" which goes straight to a first-seat checkout.
   Backend: GET /api/storefront/:slug + GET /api/storefront/event/:id. */

const money = (currency, value) =>
  `${currency} ${Number(value).toFixed(2)}`;

/* A community leader's code, from her booking link tornea.app/<slug>?sf_vc=CODE.
   Read from the URL *and* sessionStorage: usePersistUrlParams moves query params into
   sessionStorage in an App-level effect, but child effects run FIRST, so on the very
   first render the code is still only in the URL. Checking both removes that race. */
const communityCode = () =>
  new URLSearchParams(window.location.search).get('sf_vc')
  || sessionStorage.getItem('sf_vc')
  || '';

const EventCard = ({ slug, event, brandColor, initialOpen = false, hideDate = false, square = false }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(initialOpen);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cart, setCart] = useState(() => readCart(event.id));
  // Tap the event visual to open it full-screen (lightbox).
  const [lightbox, setLightbox] = useState(false);

  const loadCatalog = async () => {
    if (catalog || loading) return;
    setLoading(true);
    try {
      // A community leader's code (from her booking link) reveals her community's
      // otherwise not-public team rate — without it that rate is not in the catalog at all.
      const vc = communityCode();
      setCatalog(await apiRequest(
        'GET', `/storefront/event/${event.id}${vc ? `?vc=${encodeURIComponent(vc)}` : ''}`));
    } catch (err) {
      setError(t(err.message, { defaultValue: err.message }));
    } finally {
      setLoading(false);
    }
  };

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    loadCatalog();
  };

  // Bravos Season card opens with its catalog already loaded (no extra tap).
  useEffect(() => {
    if (initialOpen && event.has_shop) loadCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close the full-screen visual on Escape.
  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  const add = (rateId, delta) => {
    const next = { ...cart, [rateId]: Math.max((Number(cart[rateId]) || 0) + delta, 0) };
    if (!next[rateId]) delete next[rateId];
    setCart(next);
    writeCart(next, event.id);
  };

  const count = cartCount(cart);
  // The event's own app branding (project config) wins over the BU colour —
  // the API already resolves that order into event.primary_color.
  const color = event.primary_color || brandColor;
  const accent = color ? { backgroundColor: color } : undefined;

  const dateLabel = event.date
    ? new Date(event.date).toLocaleDateString(i18n.language, {
        weekday: 'short', day: 'numeric', month: 'long', year: 'numeric',
      })
    : null;

  return (
    <div className="auth__card storefront__event">
      {event.image_link && (
        <img
          className={`storefront__event-image${square ? ' storefront__event-image--square' : ''}`}
          src={event.image_link}
          alt=""
          onClick={() => setLightbox(true)}
        />
      )}
      {lightbox && event.image_link && (
        <div className="storefront__lightbox" onClick={() => setLightbox(false)} role="presentation">
          <img className="storefront__lightbox-img" src={event.image_link} alt={event.name} />
          <button type="button" className="storefront__lightbox-close" aria-label={t('close')}>×</button>
        </div>
      )}
      <h2 className="storefront__event-name">{event.name}</h2>
      <p className="storefront__event-meta">
        {[hideDate ? null : dateLabel, event.location_name, event.country]
          .filter(Boolean).join(' · ')}
      </p>
      {event.short_desc && <p className="auth__hint">{event.short_desc}</p>}

      {/* On the day itself the free way in for the people who came to cheer leads the card;
          the shop below stays for what can still be bought on site. */}
      {event.supporter_access_today && (
        <Link to={`/${slug}/support/${event.id}`} className="auth__primary storefront__support" style={accent}>
          <Icon name="megaphone" />
          {t('supporterAccessButton')}
        </Link>
      )}

      {event.has_shop ? (
        <button type="button" className="auth__primary" style={accent} onClick={toggle}>
          <Icon name="ticket" />
          {open ? t('hide') : t('viewTickets')}
        </button>
      ) : (
        <p className="auth__hint storefront__closed">{t('salesNotOpen')}</p>
      )}

      {open && (
        <div className="storefront__catalog">
          {loading && <span className="auth__spinner" />}
          {error && <p className="auth__hint">{error}</p>}
          {/* The edition reached its team cap: the team rates are gone from the catalog, so
              say why and offer the waiting list (a CRM form) instead of an empty shop. */}
          {catalog && catalog.team_cap && catalog.team_cap.full && (
            <div className="storefront__full">
              <p className="storefront__full-title">{t('teamsFullTitle')}</p>
              <p className="auth__hint">{t('teamsFullBody')}</p>
              {catalog.team_cap.waiting_list_url && (
                <a className="auth__primary" style={accent} href={catalog.team_cap.waiting_list_url}>
                  {t('joinWaitingList')}
                </a>
              )}
            </div>
          )}
          {catalog && catalog.products.length === 0 && !(catalog.team_cap && catalog.team_cap.full) && (
            <p className="auth__hint">{t('noProductsAvailable')}</p>
          )}
          {catalog && catalog.products.map((p) => {
            // VAT is a per-RATE percentage (all of a product's tickets share
            // it); shown next to every price when the shop charges VAT, so the
            // catalog matches what the checkout then splits out.
            const vat = catalog.with_vat && p.vat_rate ? Number(p.vat_rate) : 0;
            const vatNote = vat ? t('plusVatShort', { rate: vat, defaultValue: `+${vat}% VAT` }) : '';
            const inclLabel = vat ? t('inclVat', { defaultValue: 'incl. VAT' }) : '';
            const withVat = (v) => Number(v) * (1 + vat / 100);
            // Individual tickets are shown VAT-inclusive; a "company ticket"
            // (whole-team package a company buys) stays NET with the +VAT note.
            // ★ HT brands (Hercules Trophy) sell to companies, so their whole catalog
            //   is quoted NET — matching their invoices and the checkout's VAT split.
            //   PLG/Bravos sell to people and keep the VAT-inclusive quote.
            const showIncl = vat && !p.company_only && catalog.business_model !== 'HT';
            const seatIncl = vat && catalog.business_model !== 'HT';
            const priceMain = showIncl ? withVat(p.effective_price) : p.effective_price;
            const priceStrike = showIncl ? withVat(p.unit_price) : p.unit_price;
            // A discount that ends on a date sells itself: print the deadline
            // under the real price. Open-ended discounts (no ValidTo) print nothing.
            const until = p.discount_pct ? shortDate(p.discount_until, i18n.language) : null;
            return (
            <div key={p.rate_id} className="storefront__product">
              {/* One grid row, four cells: photo · description (name + what's in the
                  package) · price (right-aligned, VAT note on its own line under the
                  amount) · buy actions. The actions sit under the description on a
                  laptop and on their own centred row on a phone — that's the grid
                  areas in Storefront.css, not a second copy of the markup.
                  A discounted price stacks the strike-through amount ABOVE the real one. */}
              {p.image_link && (
                <img className="storefront__product-img" src={p.image_link} alt="" />
              )}
              <span className="storefront__product-name">
                {p.name}
                {/* Only present when her code unlocked this rate — says whose community
                    the booker is joining, so an unlocked rate never looks like a
                    mystery extra that appeared out of nowhere. */}
                {p.is_community && catalog.community && (
                  <span className="storefront__community-tag">
                    {t('viaCommunity', { community: catalog.community.name })}
                  </span>
                )}
                {/* The solo ticket needs its promise spelled out: who it is for and what
                    happens after paying. Copy lives in the translations (no rate field). */}
                {p.is_solo_product && (
                  <span className="storefront__solo-pitch">
                    <b>{t('soloPitchTitle')}</b>
                    <span>{t('soloPitchBody')}</span>
                    <span className="storefront__solo-steps">
                      <span>1. {t('soloPitchStep1')}</span>
                      <span>2. {t('soloPitchStep2')}</span>
                      <span>3. {t('soloPitchStep3')}</span>
                    </span>
                  </span>
                )}
                {/* What the package hands out — inside the description column, so the
                    picture keeps the full height of the row next to it. */}
                {p.tickets && p.tickets.length > 0 && (
                  <span className="storefront__product-tickets">
                    {p.tickets.map((tk, i) => (
                      <span key={i} className="storefront__ticket-chip">
                        <Icon name="ticket" />
                        {tk.emoji ? `${tk.emoji} ` : ''}
                        {tk.quantity}× {tk.translation_key
                          ? t(tk.translation_key, { defaultValue: tk.name })
                          : tk.name}
                      </span>
                    ))}
                  </span>
                )}
              </span>
              <span className="storefront__product-price">
                {p.discount_pct ? (
                  <s className="storefront__product-strike">
                    {money(catalog.currency, priceStrike)}
                  </s>
                ) : null}
                <span className="storefront__product-price-now">
                  {money(catalog.currency, priceMain)}
                  {showIncl
                    ? <span className="storefront__vat-note">{inclLabel}</span>
                    : (vatNote && <span className="storefront__vat-note">{vatNote}</span>)}
                </span>
                {until && (
                  <span className="storefront__discount-until">
                    {t('discountUntil', { date: until })}
                  </span>
                )}
              </span>
              <div className="storefront__product-actions">
                {/* A solo ticket (Meet the Ladies) is its own product: ONE seat for a lady
                    without a team, matched after paying. Never through the cart. */}
                {p.is_solo_product ? (p.seat_price != null && (
                  <button type="button" className="storefront__buy" style={accent}
                          onClick={() => {
                            sessionStorage.setItem('sf_mode', 'first_seat');
                            sessionStorage.setItem('sf_rate', p.rate_id);
                            navigate(`/${slug}/checkout/${event.id}?sf_mode=first_seat&sf_rate=${p.rate_id}`);
                          }}>
                    <Icon name="heart" />
                    {t('bookSoloTicket')}
                  </button>
                )) : cart[p.rate_id] ? (
                  <span className="storefront__qty">
                    <button type="button" onClick={() => add(p.rate_id, -1)}>−</button>
                    <b>{cart[p.rate_id]}</b>
                    <button type="button" onClick={() => add(p.rate_id, 1)}>+</button>
                  </span>
                ) : (
                  <button type="button" className="storefront__buy" style={accent}
                          onClick={() => add(p.rate_id, 1)}>
                    <Icon name="shopping-cart" />
                    {t('addToCart')}
                  </button>
                )}
                {!p.is_solo_product && p.seat_price != null && p.seats_total > 1 && (
                  <button
                    type="button" className="storefront__seat-buy"
                    style={color ? { borderColor: color, color } : undefined}
                    onClick={() => {
                      // The global usePersistUrlParams hook strips query params
                      // on arrival, so stash the intent in sessionStorage too.
                      sessionStorage.setItem('sf_mode', 'first_seat');
                      sessionStorage.setItem('sf_rate', p.rate_id);
                      // Carry her community code through: the checkout needs it both to
                      // SEE this rate (it is not public) and to attribute the booking.
                      const vc = communityCode();
                      if (vc) sessionStorage.setItem('sf_vc', vc);
                      navigate(`/${slug}/checkout/${event.id}?sf_mode=first_seat&sf_rate=${p.rate_id}`);
                    }}
                  >
                    {/* A single seat is an individual purchase → VAT-inclusive, unless
                        this is an HT brand, whose whole catalog is quoted NET. */}
                    {t('buyOnlyMySeat', {
                      price: money(catalog.currency,
                        seatIncl ? withVat(p.seat_price) : p.seat_price),
                    })}
                    {vat ? ` ${seatIncl ? inclLabel : vatNote}` : ''}
                  </button>
                )}
              </div>
            </div>
            );
          })}
          {count > 0 && (
            <button
              type="button" className="auth__primary storefront__checkout-bar" style={accent}
              onClick={() => {
                sessionStorage.removeItem('sf_mode');   // stale first-seat intent
                sessionStorage.removeItem('sf_rate');
                const vc = communityCode();
                if (vc) sessionStorage.setItem('sf_vc', vc);
                navigate(`/${slug}/checkout/${event.id}`);
              }}
            >
              <Icon name="shopping-cart" />
              {t('checkout')} · {count}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const Storefront = () => {
  const { t } = useTranslation();
  const { slug } = useParams();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // Existing account? Offer the way in from the brand page itself.
  const [loggedIn, setLoggedIn] = useState(false);
  useEffect(() => {
    apiRequest('GET', '/storefront/session')
      .then((s) => setLoggedIn(Boolean(s.logged_in)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      // Drop any slug left by a storefront visited earlier in THIS tab before we know
      // this one resolves. sessionStorage survives the whole tab, so an unresolved brand
      // used to leave the previous one in place and the login pinned itself to the wrong
      // business — see the brand-is-a-hint fallback in post_login.
      sessionStorage.removeItem('brandLoginSlug');
      try {
        const res = await apiRequest('GET', `/storefront/${slug}`);
        setData(res);
        if (res && res.brand) {
          // Remember where the visitor came from: /branding follows this
          // brand (colour, background photo, manifest, touch icon) on the
          // whole auth surface — /login included — until an event session
          // takes over after login. handleBranding does the applying.
          localStorage.setItem('brandSlug', res.brand.slug || slug);
          // Session-scoped signal that this login is happening UNDER a brand: the
          // login POSTs it so the server pins the session to this business and routes
          // straight to its home (no cross-business role picker). Only a real
          // storefront visit sets it, so a cold tab to tornea.app/login stays
          // un-scoped. Not a ?slug= query param — usePersistUrlParams would strip it;
          // not localStorage.brandSlug — that persists the last brand ever visited.
          sessionStorage.setItem('brandLoginSlug', res.brand.slug || slug);
          handleBranding();
        }
      } catch (err) {
        setError(t(err.message, { defaultValue: err.message }));
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, t]);

  const brand = data && data.brand;
  const events = (data && data.events) || [];
  // Bravos: the Season box is labelled "Memberships"; the first non-Season (Lab) box "Tickets".
  const firstLabId = brand && brand.is_bravos
    ? (events.find((e) => e.id !== brand.season_event_id) || {}).id
    : null;

  return (
    <AuthShell hideBrand wide>
      <div className="storefront">
        {/* Way in for existing accounts — a small pill at the top-right of the
            content column. In-flow (not position:fixed) so it stays put on
            desktop too, where .auth is a centred, capped column and a fixed
            pill drifted off to the screen corner. */}
        <div className="storefront__corner">
          <Link to={loggedIn ? '/home' : '/login'} className="storefront__corner-btn">
            <Icon name={loggedIn ? 'user' : 'sign-in-alt'} />
            {loggedIn ? t('openApp') : t('login', { defaultValue: 'Log in' })}
          </Link>
        </div>
        {loading ? (
          <div className="auth__card"><span className="auth__spinner" /></div>
        ) : !brand ? (
          <div className="auth__card"><p>{error || t('storefrontNotFound')}</p></div>
        ) : (
          <>
            <div className="storefront__head">
              {/* Bravos storefronts don't repeat the logo up top (the brand name is enough). */}
              {!brand.is_bravos && brand.logo_url && (
                <img className="storefront__logo" src={brand.logo_url} alt={brand.name} />
              )}
              <h1 className="auth__title">{brand.name}</h1>
            </div>
            {/* Free-entry "journey" panel:
                1. Are you ready? (executive → CxO pulse / employee → employee pulse)
                2. Is your company ready? (readiness scan / culture pulse)
                3. Create your free account — the rung between the teasers and paying.
                4. Become a member — scrolls to the memberships/labs below. */}
            {(() => {
              const step1 = brand.pulse_slug || brand.cxo_pulse_slug;              // employee/exec choice
              const step2 = brand.readiness_slug || brand.culture_pulse_slug;      // company readiness/culture choice
              const step3 = brand.free_account;                          // free account (needs a Season)
              const step4 = brand.is_bravos && events.length > 0;        // membership/labs live below
              if (!step1 && !step2 && !step3 && !step4) return null;
              let n = 0;
              const num = () => { n += 1; return n; };
              const numStyle = brand.primary_color ? { background: brand.primary_color } : undefined;
              const scrollToMemberships = () => {
                const el = document.getElementById('sf-memberships');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              };
              return (
                <div className="storefront__journey">
                  <p className="storefront__journey-head">{t('journeyHeading')}</p>

                  {step1 && (
                    <div className="storefront__journey-step storefront__journey-step--choice">
                      <span className="storefront__journey-num" style={numStyle}>{num()}</span>
                      <span className="storefront__journey-body">
                        <span className="storefront__journey-q">{t('journeyYou')}</span>
                        <span className="storefront__journey-sub">{t('journeyYouSub')}</span>
                        <span className="storefront__journey-choices">
                          {brand.pulse_slug && (
                            <Link to={`/pulse/${brand.pulse_slug}`} className="storefront__journey-choice">
                              <Icon name="user" className="me-1" />{t('journeyEmployee')}
                            </Link>
                          )}
                          {brand.cxo_pulse_slug && (
                            <Link to={`/cxo-pulse/${brand.cxo_pulse_slug}`} className="storefront__journey-choice">
                              <Icon name="briefcase" className="me-1" />{t('journeyExecutive')}
                            </Link>
                          )}
                        </span>
                      </span>
                    </div>
                  )}

                  {step2 && (
                    <div className="storefront__journey-step storefront__journey-step--choice">
                      <span className="storefront__journey-num" style={numStyle}>{num()}</span>
                      <span className="storefront__journey-body">
                        <span className="storefront__journey-q">{t('journeyCompany')}</span>
                        <span className="storefront__journey-sub">{t('journeyCompanySub')}</span>
                        <span className="storefront__journey-choices">
                          {brand.readiness_slug && (
                            <Link to={`/readiness/${brand.readiness_slug}`} className="storefront__journey-choice">
                              <Icon name="list-check" className="me-1" />{t('journeyReadiness')}
                            </Link>
                          )}
                          {brand.culture_pulse_slug && (
                            <Link to={`/culture-pulse/${brand.culture_pulse_slug}`} className="storefront__journey-choice">
                              <Icon name="users" className="me-1" />{t('journeyCulture')}
                            </Link>
                          )}
                        </span>
                      </span>
                    </div>
                  )}

                  {step3 && (
                    /* A Link, and NOT the --choice variant: that one sets cursor:default (it only
                       wraps the pills inside it) and would kill the hover on a whole-row target. */
                    <Link to={`/create-account/${slug}`} className="storefront__journey-step">
                      <span className="storefront__journey-num" style={numStyle}>{num()}</span>
                      <span className="storefront__journey-body">
                        <span className="storefront__journey-q">{t('journeyAccount')}</span>
                        <span className="storefront__journey-sub">{t('journeyAccountSub')}</span>
                      </span>
                      <Icon name="arrow-right" className="storefront__journey-arrow" />
                    </Link>
                  )}

                  {step4 && (
                    <button type="button" className="storefront__journey-step" onClick={scrollToMemberships}>
                      <span className="storefront__journey-num" style={numStyle}>{num()}</span>
                      <span className="storefront__journey-body">
                        <span className="storefront__journey-q">{t('journeyMember')}</span>
                        <span className="storefront__journey-sub">{t('journeyMemberSub')}</span>
                      </span>
                      <Icon name="arrow-down" className="storefront__journey-arrow" />
                    </button>
                  )}
                </div>
              );
            })()}
            <div id="sf-memberships" style={{ scrollMarginTop: '1rem' }} />
            {events.length === 0 ? (
              <div className="auth__card"><p>{t('noUpcomingEvents')}</p></div>
            ) : !brand.is_bravos ? (
              <p className="storefront__section-label">{t('upcomingEvents')}</p>
            ) : null}
            {events.map((event) => {
              const isSeason = brand.season_event_id === event.id;
              return (
                <React.Fragment key={event.id}>
                  {brand.is_bravos && isSeason && (
                    <p className="storefront__section-label">{t('membershipsHeading')}</p>
                  )}
                  {brand.is_bravos && event.id === firstLabId && (
                    <p className="storefront__section-label">{t('ticketsHeading')}</p>
                  )}
                  <EventCard slug={slug} event={event}
                             brandColor={brand.primary_color}
                             initialOpen={isSeason}
                             hideDate={isSeason}
                             square={brand.is_bravos && !isSeason} />
                </React.Fragment>
              );
            })}

            {/* Our paperwork + a way to reach a human, between the events and the
                partner wall: both are things you go looking for after the prices. */}
            <StorefrontHelp documents={brand.documents} contact={brand.contact}
                            brandName={brand.name} accentColor={brand.primary_color} />

            {/* Bottom CTA to the brand's commercial/marketing site (any business model).
                It sits *under* the help block: someone who came here to buy wants the
                trade licence and a human first — the marketing site is the way out. */}
            {brand.commercial_url && (
              <a className="storefront__learn-more" href={brand.commercial_url}
                 target="_blank" rel="noopener noreferrer"
                 style={brand.primary_color
                   ? { borderColor: brand.primary_color, color: brand.primary_color } : undefined}>
                <Icon name="globe" className="me-2" />
                {t('storefrontLearnMore', { brand: brand.name })}
              </a>
            )}

            {/* Sponsors, at the very bottom of the storefront. */}
            {(brand.sponsors || []).filter((s) => s.photo_url).length > 0 && (
              <div className="storefront__sponsors">
                <p className="storefront__section-label">{t('sponsors')}</p>
                {/* One column per sponsor type, most important first (the API
                    orders them), each cut off from the next by a rule; untyped
                    sponsors trail as a last, unlabelled column. The wall clips
                    the row's overhanging left edge so the rule vanishes on the
                    first column of every wrapped line -- see the stylesheet. */}
                <div className="storefront__sponsor-wall">
                  <div className="storefront__sponsor-row">
                    {groupSponsorsByType(brand.sponsors.filter((s) => s.photo_url)).map((group) => (
                      <div className="storefront__sponsor-group" key={group.type || '_untyped'}>
                        {group.type && (
                          <p className="storefront__sponsor-type">{group.type}</p>
                        )}
                        <div className="storefront__sponsor-grid">
                          {group.items.map((s) => {
                            const logo = (
                              <img className="storefront__sponsor-logo" src={s.photo_url}
                                   alt={s.name || ''} />
                            );
                            // The key carries the type too: a sponsor tagged with several
                            // types appears once in each of its groups.
                            const key = `${group.type || ''}-${s.id}`;
                            return s.url ? (
                              <a key={key} className="storefront__sponsor" href={s.url}
                                 target="_blank" rel="noopener noreferrer">{logo}</a>
                            ) : (
                              <span key={key} className="storefront__sponsor">{logo}</span>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AuthShell>
  );
};

export default Storefront;
